/** @jsx jsx */

import React, {
  Fragment as _,
  useLayoutEffect,
  createContext,
  useEffect,
  useState,
} from 'react';
import { css, jsx } from '@emotion/core';
import { connect, useSelector, useDispatch } from 'react-redux';
import Header from 'components/Header';
import { NextState } from 'store/next.ts';
import { setActiveTopicAction, focusBlockAction } from 'store/next';
import {
  getBlock,
  getTopicGroupFromState,
  getTopicsFromGroup,
  getInteractionsFromTopicAndState,
} from 'selectors/block';
import { Box } from '@chameleoncreator/creator-ui';
import Interactions from './Interactions';
import { ThemeProvider } from 'emotion-theming';
import { theme } from 'theme';
import Bookmark from './Bookmark';
import { ErrorModal } from 'components/errorModal';
import { FontManager } from './Fonts';
import TopicContainer from './TopicContainer';
import { SCORMRenderer } from '../lib/Scorm/SCORM';
import { Block } from '@chameleoncreator/creator-app';
import { createBlock } from 'factories/block';
import { createScorm } from 'factories/scorm';
import getCurrentLocation from 'services/getCurrentLocation';
import getTopics from 'services/apiHelpers/getTopics';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import { updateInteractionsOpenAction } from 'store/next';
import getStore from 'services/getStore';
import windowScrollInfo from './windowScrollInfo';
import Interactions_Editor from './Interactions_Editor';
import Interactions_Module from './Interactions_Module';
import pubSub, { usePubSub } from 'services/pubSub';

window.location.hash = '';

/**
 * close Window run the close function on the window that matters. It does so asynchronously
 *
 * TODO: remove the magic number here
 */
const closeWindow = () => {
  setTimeout(() => {
    if (window.self === window.top) {
      window.close();
    } else {
      window.top.close();
    }
  }, 100);
};

export const ModuleContext = createContext({
  passMark: 80,
  exit: closeWindow,
});

const Module = props => {
  //
  const state = useSelector((state: any) => state);

  // create re_selectors for these ?
  const { currentTopic: topicId, currentInteraction: interactionId } = state;

  const { blocks, rootBlockId } = state;

  const group = getTopicGroupFromState(state);
  const topicList = getTopicsFromGroup(state, group);

  const dispatch = useDispatch();

  const currentTopicIndex = topicId
    ? topicList.findIndex(t => t.id === topicId)
    : 0;

  const setCurrentBlock = data => {
    dispatch(focusBlockAction(data));
  };

  const updateInteractionsSeen = id => {
    dispatch({
      type: 'UPDATE_INTERACTIONS_SEEN',
      data: {
        id,
      },
    });
  };

  const updateInteractionsOpen = id => {
    dispatch(updateInteractionsOpenAction(id));
  };

  useLayoutEffect(() => {
    windowScrollInfo(null, topicId);
  }, [topicId]);

  useEffect(() => {
    // put this somewhere else
    const compareIds = (topicInteraction, id) => {
      // purposefully using double == here for string / number
      // return (topicInteraction.props.sId || topicInteraction.id) == id;

      // or. be sure we're comparing strings

      return [topicInteraction.props.sId, topicInteraction.id]
        .map(sId => String(sId))
        .includes(id);
    };

    const navToHash = e => {
      e.preventDefault();

      let hash: any = String(window.location.hash)
        .replace(/#/, '')
        .split('/');

      if (hash.length !== 2) {
        window.locationHistory = null;
        return;
      }

      // for legacy we'll use the id. format will be the same as
      const [topicSid, interactionSId] = hash;

      const targetTopic = getTopics().find((t: any) => compareIds(t, topicSid));

      // this'll only be for when we're testing
      if (!targetTopic) {
        return;
      }

      const interactionsInTopic = getInteractionsInTopic(targetTopic);
      const targetInteraction = interactionsInTopic.find((i: any) =>
        compareIds(i, interactionSId)
      );

      // be safe. bail if not
      if (!targetInteraction) {
        return;
      }

      // if we're here. we're gonna nav
      // set history
      window.locationHistory = getCurrentLocation();
      //

      const targetInteractionIndex = interactionsInTopic.findIndex((i: any) =>
        compareIds(i, interactionSId)
      );

      setCurrentTopic(targetTopic.id);

      interactionsInTopic.forEach((i: any, index) => {
        if (index < targetInteractionIndex) {
          updateInteractionsSeen(i.id);
          updateInteractionsOpen(i.id);
        }
      });

      // tick until we have the interaction in the DOM
      const tick = setInterval(() => {
        const i = document.querySelector(`#i${targetInteraction.id}`);
        if (i) {
          clearInterval(tick);
          i.scrollIntoView();
        }
      }, 0);
    };

    window.addEventListener('hashchange', navToHash);

    // maybe we can toggle all this on / off when scrolling
  }, []);

  useLayoutEffect(() => {
    if (!rootBlockId) {
      return;
    }
    window.scrollTo(0, 0);
  }, [currentTopicIndex]);

  if (!rootBlockId) {
    return null;
  }

  const topic = topicList[currentTopicIndex > -1 ? currentTopicIndex : 0];

  // update has with topic name
  // do we want to keep the spaces ' ' > %20
  // assume screen readers etc know how to manage them ( and not read that as literal )
  // const parsedTopicName = topic.props.name.replace(/#/g, '');
  // window.location.hash = parsedTopicName;

  let interactions = topic
    ? getInteractionsFromTopicAndState(state, getBlock(blocks, topic.id))
    : [];

  let scormBlock = Object.values(blocks).find(
    (b: Block) => b.type === 'SCORM'
  ) as Block;

  if (!scormBlock) {
    scormBlock = createScorm();
  }

  const moduleBlock = Object.values(blocks).find(
    (b: Block) => b.type === 'Module'
  ) as Block;
  let passMark = moduleBlock.props.passMark || 80;

  // console.log('MODULE RE_RENDERS !!!', interactions);

  return (
    <ModuleContext.Provider value={{ passMark, exit: closeWindow }}>
      <FontManager />
      <ErrorModal>
        {error => (
          <div>
            <h1>Oh no :(</h1>
            <p>Something has gone wrong. The error message provided is:</p>
            <p>
              <strong>"{error.message}".</strong>
            </p>
            <p>
              We have been alerted to this error, however if it is consistently
              occuring please fell free to notify us by clicking on the support
              button in the bottom right hand corner.
            </p>
          </div>
        )}
      </ErrorModal>
      <SCORMRenderer key="scorm" {...scormBlock} />
      <ThemeProvider theme={theme}>
        <Header currentTopicIndex={currentTopicIndex} topics={topicList} />
        <TopicContainer
          key={`topic${currentTopicIndex}`}
          state={state}
          topic={topic}
        >
          {window.dev() ? (
            <Interactions_Editor
              currentTopicId={topicId}
              currentInteractionId={interactionId}
              interactions={interactions}
            />
          ) : (
            <Interactions_Module
              key={interactions[0]?.id}
              interactions={interactions}
            />
          )}
        </TopicContainer>
        {/* <TopicContainer /> */}
        <div id="modal-container" role="dialog" />
      </ThemeProvider>
      <Bookmark />
    </ModuleContext.Provider>
  );
};

export default Module;
